import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/Alert';
import apiClient from '../utils/api';
import { useAuth } from '../context/AuthContext';

const GoogleCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(true);
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent multiple calls to the backend with the same code
    if (hasProcessed.current) {
      return;
    }

    const handleCallback = async () => {
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setError('Google authentication was cancelled or failed');
        setProcessing(false);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      if (!code) {
        setError('No authorization code received from Google');
        setProcessing(false);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        hasProcessed.current = true;
        // Exchange the authorization code - token is now stored in httpOnly cookie by backend
        await apiClient.get(
          `/api/auth/google/callback?code=${code}`
        );

        // Fetch user info so AuthContext knows we're authenticated
        await refreshUser();

        // Redirect to profile page after successful authentication
        navigate('/home');
      } catch (err) {
        console.error('Google callback error:', err);
        setError(err.response?.data?.detail || 'Failed to authenticate with Google');
        setProcessing(false);
        // Reset the flag on error so user can retry
        hasProcessed.current = false;
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-elev py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {processing ? (
            <>
              <div className="flex justify-center mb-6">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-fg-3"></div>
              </div>
              <h2 className="text-2xl font-medium text-fg mb-2">
                Completing Google Sign-In...
              </h2>
              <p className="text-fg-3">
                Please wait while we finish setting up your account.
              </p>
            </>
          ) : (
            <>
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <p className="text-fg-3">
                Redirecting you back to login...
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleCallbackPage;

